import { UISDK } from "@/ui-sdk";
import { Product } from "@/ui-sdk/types";
import { fetchProducts, sortProduct } from "@/utils";
import { getLowTicketSizeRecommendationFromHandles } from "../low-ticket-recommendation/utils/get-low-ticket-size-recommendations";
import { cleanName } from "../product-list/util";
import { CDN_URL } from "@/constants/cdn";

export default async function handleRecommendationWithoutDrawer(
  element: HTMLElement,
  sdk: UISDK,
) {
  const cdn = CDN_URL;
  const handle = window?.location?.pathname?.split("/").at(-1);
  const key = Date.now();

  const [productResult, similarIdsResult, complementaryIdsResult] =
    await Promise.allSettled([
      window.helium.datastore.getProduct({ id: handle }),
      window.helium.datastore.getSimilar({ id: handle ?? "" }),
      window.helium.datastore.getComplementary({ id: handle ?? "" }),
    ]);

  let product: Product | null = null;
  let similarIds: string[] | null = null;
  let complementaryIds: Record<string, any> | null = null;

  if (productResult.status === "fulfilled") {
    product = productResult.value;
    if (!product?.details?.id && !product?.id) return;
  } else {
    console.error("Failed to get product:", productResult.reason);
  }

  if (similarIdsResult.status === "fulfilled") {
    similarIds = similarIdsResult.value;
  } else {
    console.error("Failed to get similar IDs:", similarIdsResult.reason);
  }

  if (complementaryIdsResult.status === "fulfilled") {
    complementaryIds = complementaryIdsResult.value;
  } else {
    console.error(
      "Failed to get complementary IDs:",
      complementaryIdsResult.reason,
    );
  }

  let complementaryStyles: Record<
    string,
    {
      position: string;
      results: string[];
      subcategory: string;
    }[]
  > | null = null;

  let allStyles: any = null;
  let lowTicketSize: any = null;

  try {
    const res = await fetch(
      `${cdn}/${window.helium.brandData.orgId}/complementary-styles/v3/${handle}/results.json`,
    );
    if (res.ok) {
      complementaryStyles = await res.json();
    }
  } catch (error) {
    console.error("Failed to fetch complementary styles:", error);
  }
  try {
    const res = await fetch(
      `${cdn}/${window?.helium.brandData.orgId}/complementary-styles/styles/${cleanName(product?.tags?.subcategory?.toLowerCase())}.${window.helium.brandData.storeId}.json`,
    );
    if (res.ok) {
      allStyles = await res.json();
    }
  } catch (error) {
    console.error("Failed to fetch all styles:", error);
  }

  try {
    lowTicketSize = await getLowTicketSizeRecommendationFromHandles([
      window.location.pathname.split("/products/").at(1) ?? "custom",
    ]);
  } catch (error) {
    console.error("Failed to fetch low ticket size recommendations:", error);
  }

  const filteredLowTicketSize =
    lowTicketSize && typeof lowTicketSize === "object"
      ? Object.fromEntries(
          Object.entries(lowTicketSize)
            .filter(([_, value]) => value?.complementary?.length > 0)
            .map(([key, value]) => {
              let text = key;
              if (text.toLowerCase() === "handbags") {
                text = "Yoga Mat";
              }
              return [text, value];
            }),
        )
      : {};

  let currentPosition = null;

  if (
    Array.isArray(allStyles) &&
    allStyles.length > 0 &&
    allStyles[0]?.otherSubcategoryPositions &&
    typeof allStyles[0].otherSubcategoryPositions === "object"
  ) {
    const entries = Object.entries(allStyles[0].otherSubcategoryPositions)
      .filter((e: any) => true) // this line currently does nothing
      .filter(Boolean);

    if (entries.length > 0 && Array.isArray(entries[0])) {
      currentPosition = entries[0][0];
    }
  }
  // Deep clone with a sanity check
  const complementaryStylesWithProduct =
    complementaryStyles && typeof complementaryStyles === "object"
      ? JSON.parse(JSON.stringify(complementaryStyles))
      : {};

  const totalIds = [];

  for (const style in complementaryStyles) {
    const prods = Object.values(complementaryStyles[style]);

    totalIds.push(...prods);
  }

  const productResults = await Promise.allSettled(
    totalIds.map(
      (id) =>
        window.helium.datastore
          .getProduct({ id })
          .then((res) => res.details) as Promise<Product>,
    ),
  );

  const totalProducts = productResults
    .filter(
      (r): r is PromiseFulfilledResult<Product> => r.status === "fulfilled",
    )
    .map((r) => r.value);

  const sortedSimilarIds = similarIds
    ? await sortProduct({
        productsIds: similarIds,
      })
    : [];

  const isPairItWithEmpty =
    !complementaryIds ||
    Object.keys(complementaryIds).length === 0 ||
    Object.values(complementaryIds).every(
      (subcategory) => Object.values(subcategory).flat().length === 0,
    );

  const [similarProducts, complementaryProducts] = await Promise.all([
    sortedSimilarIds ? await fetchProducts(sortedSimilarIds) : [],
    isPairItWithEmpty
      ? []
      : Object.fromEntries(
          await Promise.all(
            Object.entries(complementaryIds).map(
              async ([category, subcategories]) => {
                if (Array.isArray(complementaryIds[category])) {
                  const products = await fetchProducts(
                    complementaryIds[category].slice(0, 5),
                  );
                  if (products.length === 0) return [category, {}];
                  return [
                    category,
                    {
                      complementary: products,
                    },
                  ];
                }
                const resolvedSubcategories = Object.fromEntries(
                  await Promise.all(
                    Object.entries(subcategories).map(
                      async ([subcategory, ids]) => {
                        return [subcategory, await fetchProducts(ids)];
                      },
                    ),
                  ),
                );

                return [category, resolvedSubcategories];
              },
            ),
          ),
        ),
  ]);
  if (
    similarProducts.length === 0 &&
    Object.keys(complementaryProducts).length === 0 &&
    Object.keys(filteredLowTicketSize).length === 0
  ) {
    return;
  }
  // TODO: Aakash to split and resuse code snippets not copy

  return sdk.renderRecommendationWithoutDrawer({
    key,
    s: similarProducts,
    c: complementaryProducts,
    currentProductHandle: handle,
    loading: false,
    complementaryStyles: complementaryStylesWithProduct,
    currentProductPosition: currentPosition,
    lowTicketSize: filteredLowTicketSize,
    element: element,
    sdk,
  });
}
